import React, { useState, useEffect } from 'react';
import './StoreDashboard.css';
import storeService from '../services/storeService';

interface StoreDashboardProps {
  storeData: any;
  onNavigate: (view: 'dashboard' | 'orders' | 'menu' | 'earnings' | 'settings') => void;
}

const StoreDashboard: React.FC<StoreDashboardProps> = ({ storeData, onNavigate }) => {
  const [stats, setStats] = useState({
    todayOrders: 0,
    pendingOrders: 0,
    todayEarnings: 0,
    activeItems: 0
  });
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewOrderAlert, setShowNewOrderAlert] = useState(false);
  const [lastOrderCount, setLastOrderCount] = useState(0);

  // Category-specific data
  const getCategoryInfo = (category: string) => {
    const categoryData: any = {
      grocery: {
        icon: '🛒',
        name: 'Grocery Store',
        nameTelugu: 'కిరాణా దుకాణం',
        color: '#06b6d4',
        features: ['Inventory Management', 'Bulk Orders', 'Loyalty Programs'],
        suggestions: ['Rice', 'Dal', 'Oil', 'Spices', 'Sugar', 'Salt']
      },
      pharmacy: {
        icon: '💊',
        name: 'Pharmacy',
        nameTelugu: 'మందుల దుకాణం',
        color: '#ef4444',
        features: ['Prescription Upload', 'Medicine Search', 'Health Tips'],
        suggestions: ['Paracetamol', 'Vitamin C', 'First Aid', 'Sanitizer', 'Masks']
      },
      vegetables: {
        icon: '🥬',
        name: 'Vegetables & Fruits',
        nameTelugu: 'కూరగాయలు & పండ్లు',
        color: '#22c55e',
        features: ['Fresh Produce', 'Daily Updates', 'Seasonal Items'],
        suggestions: ['Tomato', 'Onion', 'Potato', 'Leafy Greens', 'Fruits']
      },
      meat: {
        icon: '🥩',
        name: 'Meat & Seafood',
        nameTelugu: 'మాంసం & చేపలు',
        color: '#dc2626',
        features: ['Fresh Cut', 'Special Orders', 'Hygiene Certified'],
        suggestions: ['Chicken', 'Mutton', 'Fish', 'Prawns', 'Eggs']
      },
      bakery: {
        icon: '🍞',
        name: 'Bakery',
        nameTelugu: 'బేకరీ',
        color: '#f59e0b',
        features: ['Fresh Baked', 'Custom Orders', 'Party Orders'],
        suggestions: ['Bread', 'Cakes', 'Cookies', 'Pastries', 'Buns']
      },
      dairy: {
        icon: '🥛',
        name: 'Dairy Products',
        nameTelugu: 'పాల ఉత్పత్తులు',
        color: '#3b82f6',
        features: ['Fresh Daily', 'Quality Check', 'Home Delivery'],
        suggestions: ['Milk', 'Curd', 'Butter', 'Cheese', 'Paneer']
      },
      restaurant: {
        icon: '🍽️',
        name: 'Restaurant',
        nameTelugu: 'రెస్టారెంట్',
        color: '#8b5cf6',
        features: ['Live Menu', 'Custom Orders', 'Special Dishes'],
        suggestions: ['Biryani', 'Curry', 'Roti', 'Rice', 'Starters']
      },
      sweets: {
        icon: '🍬',
        name: 'Sweets Shop',
        nameTelugu: 'తీపి దుకాణం',
        color: '#ec4899',
        features: ['Fresh Made', 'Festival Specials', 'Bulk Orders'],
        suggestions: ['Laddu', 'Jalebi', 'Barfi', 'Halwa', 'Mysore Pak']
      },
      beverages: {
        icon: '🥤',
        name: 'Beverages',
        nameTelugu: 'పానీయాలు',
        color: '#14b8a6',
        features: ['Cold Storage', 'Variety Pack', 'Combo Offers'],
        suggestions: ['Soft Drinks', 'Juice', 'Water', 'Energy Drinks', 'Tea/Coffee']
      },
      petcare: {
        icon: '🐾',
        name: 'Pet Care',
        nameTelugu: 'పెంపుడు జంతువుల సంరక్షణ',
        color: '#f97316',
        features: ['Pet Food', 'Accessories', 'Grooming Items'],
        suggestions: ['Dog Food', 'Cat Food', 'Treats', 'Toys', 'Shampoo']
      },
      stationery: {
        icon: '📚',
        name: 'Stationery',
        nameTelugu: 'స్టేషనరీ',
        color: '#6366f1',
        features: ['School Supplies', 'Office Items', 'Art Materials'],
        suggestions: ['Notebooks', 'Pens', 'Pencils', 'Files', 'Papers']
      },
      flowers: {
        icon: '🌸',
        name: 'Flowers & Gifts',
        nameTelugu: 'పూలు & బహుమతులు',
        color: '#ec4899',
        features: ['Fresh Flowers', 'Custom Bouquets', 'Gift Wrapping'],
        suggestions: ['Roses', 'Jasmine', 'Marigold', 'Bouquets', 'Decorations']
      },
      electronics: {
        icon: '📱',
        name: 'Electronics',
        nameTelugu: 'ఎలక్ట్రానిక్స్',
        color: '#3b82f6',
        features: ['Warranty', 'Latest Models', 'Accessories'],
        suggestions: ['Mobiles', 'Chargers', 'Earphones', 'Cables', 'Power Banks']
      },
      hardware: {
        icon: '🔧',
        name: 'Hardware',
        nameTelugu: 'హార్డ్‌వేర్',
        color: '#78716c',
        features: ['Tools', 'Building Materials', 'Repair Items'],
        suggestions: ['Screws', 'Nails', 'Tape', 'Paint', 'Tools']
      },
      general: {
        icon: '🏪',
        name: 'General Store',
        nameTelugu: 'సాధారణ దుకాణం',
        color: '#6366f1',
        features: ['Variety Items', 'Daily Needs', 'Quick Service'],
        suggestions: ['Groceries', 'Household Items', 'Personal Care', 'Snacks']
      }
    };
    return categoryData[category] || categoryData.general;
  };

  const categoryInfo = getCategoryInfo(storeData.category || 'general');

  useEffect(() => {
    fetchDashboardData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [ordersResponse, itemsResponse] = await Promise.all([
        storeService.getStoreOrders(storeData._id),
        storeService.getStoreItems(storeData._id)
      ]);

      const orders = ordersResponse.orders || [];
      const today = new Date().toDateString();
      
      const todayOrders = orders.filter((o: any) => 
        new Date(o.createdAt).toDateString() === today
      );
      
      const pendingOrders = orders.filter((o: any) => 
        o.status === 'pending' || o.status === 'confirmed'
      );

      const todayEarnings = todayOrders.reduce((sum: number, o: any) => 
        sum + (o.totalAmount || 0), 0
      );

      const activeItems = itemsResponse.items?.filter((i: any) => i.available).length || 0;

      setStats({
        todayOrders: todayOrders.length,
        pendingOrders: pendingOrders.length,
        todayEarnings,
        activeItems
      });

      // Check for new orders
      if (pendingOrders.length > lastOrderCount && lastOrderCount > 0) {
        setShowNewOrderAlert(true);
        // Play notification sound
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZUQ0PVK3n77BdGAg+mtv0xnYnBSyBzvLaiTcIGWi77eefTRAMUKfj8LZjHAY4ktfyzHksBSR3x/DdkEAKFF606+uoVRQKRp/g8r5sIQUxh9Hz04IzBh5uwO/jmVENfQoAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA');
        audio.play().catch(() => {});
        // Show browser notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('🔔 New Order Received!', {
            body: `You have ${pendingOrders.length} pending orders. Tap to view.`,
            icon: '/logo192.png'
          });
        }
        setTimeout(() => setShowNewOrderAlert(false), 5000);
      }
      setLastOrderCount(pendingOrders.length);

      setRecentOrders(orders.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* New Order Alert */}
      {showNewOrderAlert && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, #22c55e, #16a34a)',
          color: 'white',
          padding: '15px 30px',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(34, 197, 94, 0.4)',
          zIndex: 9999,
          animation: 'slideDown 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer'
        }} onClick={() => onNavigate('orders')}>
          <span style={{ fontSize: '24px' }}>🔔</span>
          <span style={{ fontWeight: 'bold' }}>New Order Received! Tap to view</span>
        </div>
      )}

      {/* Category Header */}
      <div style={{
        background: `linear-gradient(135deg, ${categoryInfo.color}, ${categoryInfo.color}dd)`,
        color: 'white',
        padding: '20px',
        borderRadius: '15px',
        marginBottom: '20px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
          <span style={{ fontSize: '48px' }}>{categoryInfo.icon}</span>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>{storeData.name}</h1>
            <p style={{ margin: '5px 0 0 0', fontSize: '16px', opacity: 0.95 }}>
              {categoryInfo.name} • {categoryInfo.nameTelugu}
            </p>
          </div>
        </div>
        
        {/* Category Features */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '15px' }}>
          {categoryInfo.features.map((feature: string, idx: number) => (
            <span key={idx} style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '600'
            }}>
              ✓ {feature}
            </span>
          ))}
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card" style={{ borderLeft: '4px solid #06b6d4' }}>
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <div className="stat-value">{stats.todayOrders}</div>
            <div className="stat-label">Today's Orders</div>
          </div>
        </div>

        <div className="stat-card" style={{ borderLeft: '4px solid #f59e0b' }}>
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <div className="stat-value">{stats.pendingOrders}</div>
            <div className="stat-label">Pending Orders</div>
          </div>
        </div>

        <div className="stat-card" style={{ borderLeft: '4px solid #22c55e' }}>
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <div className="stat-value">{formatCurrency(stats.todayEarnings)}</div>
            <div className="stat-label">Today's Earnings</div>
          </div>
        </div>

        <div className="stat-card" style={{ borderLeft: '4px solid #8b5cf6' }}>
          <div className="stat-icon">🍽️</div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeItems}</div>
            <div className="stat-label">Active Items</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <h2 className="section-title">Quick Actions</h2>
        <div className="quick-actions-grid">
          <button className="action-btn" onClick={() => onNavigate('orders')}>
            <span className="action-icon">📦</span>
            <span className="action-label">View Orders</span>
          </button>
          <button className="action-btn" onClick={() => onNavigate('menu')}>
            <span className="action-icon">🍽️</span>
            <span className="action-label">Manage Menu</span>
          </button>
          <button className="action-btn" onClick={() => onNavigate('earnings')}>
            <span className="action-icon">💰</span>
            <span className="action-label">View Earnings</span>
          </button>
          <button className="action-btn" onClick={() => onNavigate('settings')}>
            <span className="action-icon">⚙️</span>
            <span className="action-label">Settings</span>
          </button>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="recent-orders-section">
        <div className="section-header">
          <h2 className="section-title">Recent Orders</h2>
          <button className="view-all-btn" onClick={() => onNavigate('orders')}>
            View All →
          </button>
        </div>

        {recentOrders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <p>No orders yet</p>
          </div>
        ) : (
          <div className="orders-list">
            {recentOrders.map((order) => (
              <div key={order._id} className="order-card-mini">
                <div className="order-info">
                  <div className="order-id">#{order.orderId || order._id.slice(-6)}</div>
                  <div className="order-items">
                    {order.items?.map((item: any) => item.name).join(', ')}
                  </div>
                  <div className="order-customer">{order.userId?.name || 'Customer'}</div>
                </div>
                <div className="order-meta">
                  <div className="order-amount">{formatCurrency(order.totalAmount)}</div>
                  <div className={`order-status status-${order.status}`}>
                    {order.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreDashboard;
